import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { EnumerationItem } from 'src/app/models/simple-enum-item.model';
import { EnumerationService } from 'src/app/services/enumeration-service';
import { ApplicationContextBoundComponent } from 'src/app/shared/components/application-context-bound.component';
import { CdContactInfo } from 'src/app/cd-request/models/closing-disclosure-doc-request.model';
import {
  CdContactCopySource,
  CdContactCopySourceProvider,
  CdContactSnapshot
} from 'src/app/modules/cd-request/components/wholesale-cd-wizard/steps/cd-contact-copy';
import { copyToClipboard } from 'src/utils';

export interface EscrowInformationFormData {
  companyName: string;
  companyLicenseId: string;
  contactName: string;
  contactLicense: string;
  contactPhone: string;
  contactEmail: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

@Component({
  selector: 'escrow-information',
  templateUrl: './escrow-information.component.html',
  styleUrls: ['./escrow-information.component.scss'],
  standalone: false
})
export class EscrowInformationComponent extends ApplicationContextBoundComponent implements OnInit, OnChanges {

  @ViewChild('escrowInfoForm') escrowInfoForm: NgForm;

  @Input() escrowInformation: CdContactInfo | undefined;

  /** When set, a "Copy From" menu offers the contact data entered on the wizard's other steps. */
  @Input() copySourceProvider: CdContactCopySourceProvider | null = null;

  formData: EscrowInformationFormData = {
    companyName: '',
    companyLicenseId: '',
    contactName: '',
    contactLicense: '',
    contactPhone: '',
    contactEmail: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  };

  isLoading: boolean = false;
  states: EnumerationItem[] = [];
  copySources: CdContactCopySource[] = [];

  constructor(
    protected readonly injector: Injector,
    private readonly _enumerationService: EnumerationService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.initializeStates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['escrowInformation']) {
      this.populateFormFromApiData();
    }
  }

  validateForm(): boolean {
    if (this.escrowInfoForm) {
      this.escrowInfoForm.form.markAllAsTouched();
      return this.escrowInfoForm.form.valid;
    }
    return true;
  }

  getFormData(): EscrowInformationFormData {
    return { ...this.formData };
  }

  onCopyFromToggled(isOpen: boolean): void {
    if (isOpen) {
      this.copySources = this.copySourceProvider?.getContactCopySources('escrowInfo') ?? [];
    }
  }

  copyFrom(source: CdContactCopySource): void {
    this.applySnapshot(source.snapshot);
    this.escrowInfoForm?.form.markAsDirty();
  }

  copyContactInfo(): void {
    const lines = [
      this.formData.companyName,
      this.formData.address,
      `${this.formData.city || ''}, ${this.formData.state || ''} ${this.formData.zip || ''}`,
      `Contact: ${this.formData.contactName || ''}`,
      `Email: ${this.formData.contactEmail || ''}`,
      `Phone: ${this.formData.contactPhone || ''}`
    ].filter(l => l && String(l).trim() !== '' && String(l).trim() !== ',');
    copyToClipboard(lines.join('\n'));
  }

  private applySnapshot(snapshot: CdContactSnapshot): void {
    this.formData.companyName = snapshot.companyName;
    this.formData.companyLicenseId = snapshot.companyLicenseId;
    this.formData.address = snapshot.address;
    this.formData.city = snapshot.city;
    this.formData.state = snapshot.state;
    this.formData.zip = snapshot.zip;
    this.formData.contactName = snapshot.contactName;
    this.formData.contactLicense = snapshot.contactLicense;
    this.formData.contactEmail = snapshot.contactEmail;
    this.formData.contactPhone = snapshot.contactPhone;
  }

  private initializeStates(): void {
    this.states = this._enumerationService.states.map((state) => ({
      value: state.value.toUpperCase(),
      name: state.name
    }));
  }

  private populateFormFromApiData(): void {
    if (!this.escrowInformation) {
      return;
    }

    this.formData.companyName = this.escrowInformation.companyName || '';
    this.formData.companyLicenseId = this.escrowInformation.companyLicenseId || '';
    this.formData.contactName = this.escrowInformation.contactFullName || '';
    this.formData.contactLicense = this.escrowInformation.contactLicenseId || '';
    this.formData.contactPhone = this.escrowInformation.contactPhoneNumber || '';
    this.formData.contactEmail = this.escrowInformation.contactEmailAddress || '';
    this.formData.address = this.escrowInformation.companyAddress || '';
    this.formData.city = this.escrowInformation.city || '';
    this.formData.state = this.escrowInformation.state || '';
    this.formData.zip = this.escrowInformation.zip || '';
  }
}
